import { Templates } from '@hellohair/types';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import { Box, Chip, Typography } from '@mui/material';
import { FC, KeyboardEvent, MouseEvent } from 'react';
import { Draggable } from 'react-beautiful-dnd';
import { useTranslation } from 'react-i18next';
import { STEP_GROUP_COLOR, stepTypeMeta } from './stepTypes';

interface StepRowProps {
  survey: Templates.SurveyTemplate;
  index: number;
  /** Opens the editing modal for this step. */
  onEdit: () => void;
}

/**
 * One collapsed step in the list: a colour-coded, draggable summary that opens
 * the editing modal. The type chip and icon identify the step; the question
 * sits on its own line, clamped to two.
 */
export const StepRow: FC<StepRowProps> = ({ survey, index, onEdit }) => {
  const { t } = useTranslation();

  const step = survey.steps[index] as Templates.StepDataBase & { type: string };
  const meta = stepTypeMeta(step.type);
  const TypeIcon = meta.icon;
  const accent = STEP_GROUP_COLOR[meta.group];
  const position = (index + 1).toLocaleString('de-DE', { minimumIntegerDigits: 2 });
  const question = meta.preview?.(step) ?? step.question;

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    event.preventDefault();
    onEdit();
  };

  return (
    <Draggable draggableId={step.id.toString()} index={index}>
      {/*eslint-disable-next-line @typescript-eslint/no-explicit-any*/}
      {(provided: any, snapshot: any) => (
        <Box
          ref={provided.innerRef}
          {...provided.draggableProps}
          onClick={onEdit}
          onKeyDown={onKeyDown}
          role="button"
          tabIndex={0}
          aria-label={`${t(meta.labelKey)} - ${question || t('editor.hint.untitledStep')}`}
          sx={{
            border: '1px solid',
            borderColor: 'divider',
            borderLeft: `4px solid ${accent}`,
            borderRadius: '6px',
            marginBottom: '6px',
            padding: '8px 10px',
            cursor: 'pointer',
            backgroundColor: snapshot.isDragging ? 'primary.main' : 'background.paper',
            '&:hover': { backgroundColor: 'action.hover' },
            '&:focus-visible': { outline: `2px solid ${accent}`, outlineOffset: '-2px' },
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px', minWidth: 0 }}>
            <Box
              {...provided.dragHandleProps}
              // The handle must not open the modal when grabbed.
              onClick={(event: MouseEvent<HTMLDivElement>) => event.stopPropagation()}
              sx={{ display: 'flex', flexShrink: 0, color: 'grey.600', cursor: 'grab' }}
              aria-label={t('editor.action.drag')}
            >
              <DragIndicatorIcon fontSize="small" />
            </Box>
            <Typography variant="body2" fontWeight="bold" sx={{ color: 'grey.800', minWidth: '22px', flexShrink: 0 }}>
              {position}
            </Typography>
            <Chip
              size="small"
              icon={<TypeIcon sx={{ color: `${accent} !important` }} fontSize="small" />}
              label={t(meta.labelKey)}
              title={t(meta.labelKey)}
              sx={{
                color: accent,
                borderColor: accent,
                backgroundColor: 'transparent',
                border: '1px solid',
                fontWeight: 600,
                minWidth: 0,
              }}
            />
            <Box sx={{ flexGrow: 1 }} />
            <EditOutlinedIcon fontSize="small" sx={{ flexShrink: 0, color: 'grey.600' }} />
          </Box>
          <Typography
            variant="body2"
            sx={{
              marginTop: '4px',
              color: question ? 'text.secondary' : 'grey.600',
              fontStyle: question ? 'normal' : 'italic',
              overflow: 'hidden',
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
            }}
          >
            {question || t('editor.hint.untitledStep')}
          </Typography>
        </Box>
      )}
    </Draggable>
  );
};
